<template>
  <div class="relative-container associated-form-section" :key="`detail-view-submission-list-${detailViewMode}`">
    <submission-filters v-if="showFilters"
                        :content-element="contentElement"
                        :linked-forms="linkedFormsForDropdownOptions"
                        @handleFilters="handleFilters">
    </submission-filters>
    <infinite-scroller-component :disabled="isPreview || !isElementFocusMode" @loadMore="fetchMore" :busy="fetchingMore" :fullyLoaded="isFullyLoaded">
      <submission-list v-if="submissionsSet"
                       :is-in-detail-view-edit-mode="isInEditMode"
                       :loading="loadingFormSubmissions"
                       :submissions="allFormSubmissions"
                       :submission-metadata="submissionMetadata"
                       :default-sort-details="sortDetails"
                       :initial-count="submissionLimit"
                       :total-count="totalCount"
                       :filters-on="filtersOn"
                       @updatePagination="updatePagination">
        <template slot="cta">
          <div>
            <router-link
              v-if="isDefaultMode"
              tag="button"
              class="btn blue"
              :to="showAllLinkRoute || isInEditMode "
              :disabled="isPreview">View All Forms</router-link>
          </div>
        </template>

      </submission-list>
    </infinite-scroller-component>
  </div>
</template>

<script lang="ts">
  import Vue from "vue";
  import SubmissionList from "~/components/content/submission-list.vue";
  import SubmissionFilters from "~/components/content/submission-filters.vue";
  import PublishedDetailView from "~/models/detailView/publishedDetailView";
  import SubmissionDecorator from "../../analyst_dashboard/models/submissionDecorator";
  import HelpText from "~/components/helpText.vue";
  import {fakeSubmissions} from "../../fixtures/formSubmissionSearchkickJson"
  import {Hash} from "~/interfaces/hashInterfaces";
  import Pagination, {IPagination} from "~/models/pagination";
  import {FormListSortColumn} from "../../detailViewBuilder/enums/formListSortColumn";
  import ColumnSortMixin from "~/mixins/columnSortMixin";
  import {StringHelper} from "~/helpers/stringHelper";
  import {Area} from "../enums/area";
  import {DetailViewMode} from "../enums/detailViewMode";
  import {mapState} from "vuex";
  import InfiniteScrollerComponent from "~/components/infiniteScroller.vue"
  import {FormListFormDropownOptionsConfig} from "../../detailViewBuilder/enums/formListFormDropownOptionsConfig";
  import ContentElementComponent from "./content-element-component.vue";
  import RefreshEntryDataBus from '../buses/refreshEntryDataBus';

  interface ILinkedForm {
    form_id: string,
    form_title: string,
    is_published: boolean,
    submissions: Array<SubmissionDecorator>,
  }

  export default Vue.extend({
    extends: ContentElementComponent,
    mixins: [
      ColumnSortMixin
    ],
    components: {
      SubmissionList,
      SubmissionFilters,
      HelpText,
      InfiniteScrollerComponent
    },
    created() {
     this.initializePagination();
    },
    async mounted() {
      this.registerEventBusListeners();
      await this.fetchLinkedFormSubmissions();
    },
    data() {
      return {
        allFormSubmissions: [],
        fakeSubmissions: JSON.parse(fakeSubmissions).map(e => new SubmissionDecorator(e)),
        pagination: new Pagination(),
        allFilters: null,
        loadingFormSubmissions: false,
        fetchingMore: false,
        submissionMetadata: {
          totalSubmissionCount: null,
          querySubmissionCount: null,
        }
      }
    },
    computed: {
      totalCount(): number {
        return this.querySubmissionCount;
      },
      isFullyLoaded(): boolean {
        return this.allFormSubmissions.length >= this.querySubmissionCount;
      },
      querySubmissionCount(): number {
        return this.submissionMetadata.querySubmissionCount
      },
      submissionLimit(): number {
        if (this.isDefaultMode) {
          return this.contentElement.record_display_limit;
        } else return 1000;
      },
      linkedFormsForDropdownOptions() {
        if (this.contentElement.filter_form_drodpown_options_config === FormListFormDropownOptionsConfig.OnlyFormsWithSubmissions) {
          return this.contentElement.linked_forms_collection.filter(lf => {
            return lf.total_submission_count && lf.total_submission_count > 0
          })
        } else {
          return this.contentElement.linked_forms_collection
        }
      },
      isDefaultMode(): boolean {
        return this.detailViewMode === DetailViewMode.Default
      },
      isElementFocusMode(): boolean {
        return this.detailViewMode === DetailViewMode.ElementFocus;
      },
      showAllLinkRoute(): { name: Area, params: Hash } {
        return { name: Area.detailViewFormList, params: { content_element_id: this.contentElement.client_id } };
      },
      showFilters(): boolean {
        return this.detailViewMode === DetailViewMode.ElementFocus ||
          ( this.detailViewMode === DetailViewMode.Default &&
            this.contentElement.enable_filters_when_embedded &&
            this.submissionMetadata.totalSubmissionCount > this.contentElement.display_filters_after_num );
      },
      submissionsSet(): boolean {
        return this.allFormSubmissions !== null
      },
      showLinkToAll(): boolean {
        return this.allFormSubmissions.length > this.contentElement.record_display_limit
      },
      sortedLinkedForms() {
        if (!this.linkedForms) return [];
        return this.linkedForms.sort((a, b) => {
          return a.title - b.title
        });
      },
      sortDetails(): {direction: IPagination["direction"], sort: IPagination["sort"] | number} {
        return this.pagination.sortAsJson();
      },
      filterParams() {
        return Object.assign({}, this.allFilters)
      },
      filtersOn(): boolean {
        if (!!this.allFilters) {
          return Object.values(this.allFilters).some(e => !!e)
        } else return false
      },
      fetchOptions(): Hash {
        // Array Proxy
        const linkedFormIds = this.contentElement.linked_form_ids.copy;
        const detailViewId = this.selectedDetailView instanceof PublishedDetailView ? this.selectedDetailView.detail_view_id : this.selectedDetailView.id;
        return {
          organizationId: this.selectedOrganization.id,
          contentElementId: this.contentElement.id,
          recordId: this.selectedObjectModelEntry.id,
          detailViewId: detailViewId,
          linkedFormIds: linkedFormIds,
          filters: this.filterParams,
          pagination: this.pagination.asJson({per_page: "page_size"}),
        }
      },
      ...mapState('detailViewStore', ['detailViewMode']),
    },
    methods: {
      registerEventBusListeners(): void {
        RefreshEntryDataBus.onRefresh(() => {
          this.fetchLinkedFormSubmissions();
        })
      },
      resetPagination(): void {
        const currentSort = this.pagination.sort;
        const currentDirection = this.pagination.direction;
        const perPage = this.pagination.per_page;

        this.pagination = new Pagination();

        this.pagination.sort = currentSort;
        this.pagination.direction = currentDirection;
        this.pagination.per_page = perPage;
      },
      initializePagination(): void {
        this.pagination.sort = this.contentElement.default_sort_by_column || FormListSortColumn.SubmissionDate;
        this.pagination.direction = this.sortDirectionString(this.contentElement.default_sort_direction || -1);
        this.pagination.per_page = this.contentElement.record_display_limit || 5;
      },
      async handleFilters(filters) {
        this.resetPagination();
        let params = {}
        Object.keys(filters).forEach(k => {
          if (!!filters[k]) {
            params[StringHelper.camelToUnderscore(k)] = filters[k];
          }
        })
        this.allFilters = params;
        await this.fetchLinkedFormSubmissions();
      },
      async updatePagination(listPaginationJson): Promise<void> {
        this.pagination.updateFrom(listPaginationJson)
        await this.fetchLinkedFormSubmissions();
      },
      async fetchAndSet(initial=true) {
        const {submissionJson, totalSubmissionCount, querySubmissionCount, pagination} = await this.selectedDetailView.provider.fetchLinkedFormSubmissions(this.fetchOptions);
        const submissions = submissionJson.map(e => new SubmissionDecorator(e))
        if (initial) {
          this.allFormSubmissions = submissions;
        } else {
          this.allFormSubmissions = this.allFormSubmissions.concat(submissions);
        }
        this.pagination.updateFrom(pagination);
        this.submissionMetadata = {
          totalSubmissionCount: totalSubmissionCount,
          querySubmissionCount: querySubmissionCount
        };
      },
      async fetchMore(): Promise<void> {
        if (this.pagination.nextPage) {
          this.pagination.turnPage(1);
        } else return // no results left
        this.fetchingMore = true;
        await this.fetchAndSet(false);
        this.fetchingMore = false;
      },

      async fetchLinkedFormSubmissions(): Promise<void> {
        this.loadingFormSubmissions = true;
        if (this.isPreview) {
          this.allFormSubmissions = this.fakeSubmissions.slice().map(s => {
            s.title = this.randomTitle();
            return new SubmissionDecorator().updateFrom(s)
          }).sort((a, b) => b["completed_at"] - a["completed_at"] );
          this.loadingFormSubmissions = false;
        } else {
          await this.fetchAndSet();
          this.loadingFormSubmissions = false;
        }
      },
      // Preview Concerns
      randomTitle(): string {
        const arr = this.contentElement.linked_forms_collection;
        return arr[Math.floor(Math.random()*arr.length)].title;
      }
    },
  });
</script>